import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "./hrmsNotifications";
import Apis from "../../utils/apis";

export const reminderApiv2 = createApi({
    reducerPath: "reminders",
    baseQuery: baseQueryWithAuth,
    tagTypes: ["Reminders"],

    endpoints: (builder) => ({
        // 🔵 GET ALL (with filter + pagination)
        getReminders: builder.query({
            query: ({ filter = "upcoming", page = 1, limit = 10 } = {}) => ({
                url: Apis.reminders,
                params: { filter, page, limit },
            }),

            // 🔥 cache per filter/page combo
            providesTags: (result) =>
                result?.data
                    ? [
                        ...result.data.map((r) => ({
                            type: "Reminders",
                            id: r._id,
                        })),
                        { type: "Reminders", id: "LIST" },
                    ]
                    : [{ type: "Reminders", id: "LIST" }],
        }),

        // 🔵 GET UNREAD (for popup badge)
        getUnreadReminders: builder.query({
            query: () => ({
                url: Apis.getUnreadReminders,
            }),
            providesTags: [{ type: "Reminders", id: "UNREAD" }],

            // optional polling
            pollingInterval: 60 * 1000,
        }),

        // 🔵 GET BY LEAD
        getReminderByLead: builder.query({
            query: (leadId) => ({
                url: `${Apis.reminders}/lead/${leadId}`,
            }),
            providesTags: (result, error, leadId) => [
                { type: "Reminders", id: leadId },
            ],
        }),

        // CREATE / UPDATE (UPSERT)
        createOrUpdateReminder: builder.mutation({
            query: (body) => ({
                url: Apis.reminders,
                method: "POST",
                body,
            }),

            invalidatesTags: [
                { type: "Reminders", id: "LIST" },
                { type: "Reminders", id: "UNREAD" },
            ],
        }),

        // MARK AS READ
        markReminderAsRead: builder.mutation({
            query: (id) => ({
                url: `${Apis.reminders}/${id}/read`,
                method: "PATCH",
            }),

            invalidatesTags: (result, error, id) => [
                { type: "Reminders", id },
                { type: "Reminders", id: "UNREAD" },
            ],
        }),

        // MARK AS COMPLETED
        markReminderCompleted: builder.mutation({
            query: (id) => ({
                url: `${Apis.reminders}/${id}/complete`,
                method: "PATCH",
            }),

            invalidatesTags: (result, error, id) => [
                { type: "Reminders", id },
                { type: "Reminders", id: "LIST" },
            ],
        }),

        // DELETE
        deleteReminder: builder.mutation({
            query: (id) => ({
                url: `${Apis.reminders}/${id}/delete`,
                method: "PATCH",
            }),

            invalidatesTags: (result, error, id) => [
                { type: "Reminders", id },
                { type: "Reminders", id: "LIST" },
            ],
        }),

        // RESCHEDULE
        rescheduleReminder: builder.mutation({
            query: ({ id, reminderTime, type }) => ({
                url: `${Apis.reminders}/${id}/reschedule`,
                method: "PATCH",
                body: { reminderTime, type },
            }),

            invalidatesTags: (result, error, { id }) => [
                { type: "Reminders", id },
                { type: "Reminders", id: "LIST" },
            ],
        }),
    }),
});


export const {
    useGetRemindersQuery,
    useGetUnreadRemindersQuery,
    useGetReminderByLeadQuery,

    useCreateOrUpdateReminderMutation,
    useMarkReminderAsReadMutation,
    useMarkReminderCompletedMutation,
    useRescheduleReminderMutation,
    useDeleteReminderMutation,
} = reminderApiv2;